import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion, MotionConfig, useReducedMotion } from 'framer-motion';
import {
  FiArrowRight,
  FiArrowUpRight,
  FiBarChart2,
  FiBookOpen,
  FiCalendar,
  FiCheck,
  FiCode,
  FiCpu,
  FiFolder,
  FiGlobe,
  FiGrid,
  FiHeart,
  FiLayers,
  FiMessageCircle,
  FiMonitor,
  FiRefreshCw,
  FiShield,
  FiTerminal,
  FiUsers,
  FiZap,
} from 'react-icons/fi';
import './ProductPage.css';

const SERVICE_URL = 'https://narnia-lab.com/server/';

const reveal = {
  hidden: { opacity: 0, y: 42 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.78,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const revealGroup = {
  hidden: {},
  visible: {
    transition: {
      delayChildren: 0.06,
      staggerChildren: 0.09,
    },
  },
};

const flowSteps = [
  {
    number: '01',
    label: 'CHOOSE',
    title: '폴더를 고릅니다.',
    copy: '새 프로젝트를 만들거나, 이어서 작업할 폴더를 선택합니다.',
    icon: FiFolder,
  },
  {
    number: '02',
    label: 'ASK',
    title: 'AI에게 말합니다.',
    copy: 'ChatGPT·Claude·Gemini 중 알맞은 AI와 자연스럽게 대화합니다.',
    icon: FiMessageCircle,
  },
  {
    number: '03',
    label: 'BUILD',
    title: '바로 실행합니다.',
    copy: '설치와 명령어를 외우지 않아도 화면과 기능을 즉시 확인합니다.',
    icon: FiCode,
  },
  {
    number: '04',
    label: 'LIVE',
    title: '링크로 공개합니다.',
    copy: '완성한 프로그램을 안전한 주소로 열고 실제 사용자와 공유합니다.',
    icon: FiGlobe,
  },
];

const productTour = [
  {
    number: '01',
    label: 'PROJECT SPACE',
    title: '프로젝트가 많아져도,\n흐름은 선명하게.',
    copy:
      '파일과 폴더를 프로젝트 단위로 정리하고, 지금 작업 중인 맥락을 AI가 함께 이해합니다.',
    image: '/product-imgs/slide-07.png',
    imageAlt: '나니아랩 프로젝트 폴더 관리 실제 화면',
    tags: ['프로젝트별 폴더', '파일 바로보기', '작업 맥락 유지'],
    tone: 'blue',
  },
  {
    number: '02',
    label: 'SMART SUPPORT',
    title: '막히는 순간에도,\n작업은 끊기지 않게.',
    copy:
      '현재 화면과 실행 상태를 바탕으로 필요한 도움을 이어 받아 오류를 찾고 다음 행동을 결정합니다.',
    image: '/product-imgs/slide-08.png',
    imageAlt: '나니아랩 스마트 서포트 실제 화면',
    tags: ['상황 기반 도움', '오류 해결', 'AI 이미지 제작'],
    tone: 'violet',
  },
  {
    number: '03',
    label: 'COLLABORATION',
    title: '혼자 만드는 화면에서,\n함께 성장하는 공간으로.',
    copy:
      '프로젝트를 공유하고 피드백을 주고받으며, 만든 결과물을 실제 사용자에게 연결합니다.',
    image: '/product-imgs/slide-09.png',
    imageAlt: '나니아랩 실시간 협업 채팅 실제 화면',
    tags: ['실시간 대화', '프로젝트 공유', '피드백 반영'],
    tone: 'cyan',
  },
];

const useCases = [
  {
    icon: FiCalendar,
    audience: 'CREATOR · RESERVATION',
    title: '수업·공연 예약 서비스',
    copy: '복잡한 외주 없이 일정 확인과 신청이 가능한 웹 서비스를 직접 만듭니다.',
    result: '예약 흐름을 하나의 링크로',
    size: 'wide',
  },
  {
    icon: FiHeart,
    audience: 'STUDENT · HEALTH',
    title: '어르신 인지 건강 앱',
    copy: '학생의 관심을 실제 사용자를 돕는 실버테크 서비스로 발전시킵니다.',
    result: '아이디어에서 앱까지',
  },
  {
    icon: FiBarChart2,
    audience: 'PLANNER · RESEARCH',
    title: 'AI 리서치 대시보드',
    copy: '흩어진 자료를 수집하고 이해하기 쉬운 화면과 리포트로 정리합니다.',
    result: '반복 업무를 자동화',
  },
  {
    icon: FiUsers,
    audience: 'TEAM · EXHIBITION',
    title: '참여형 전시 플랫폼',
    copy: '관객의 입력이 실시간 화면에 반영되는 인터랙티브 프로젝트를 구현합니다.',
    result: '현장과 연결되는 서비스',
    size: 'wide',
  },
  {
    icon: FiGrid,
    audience: 'STUDENT · GAME',
    title: '나만의 웹 게임',
    copy: '좋아하는 규칙과 세계관을 직접 설계하고 친구들이 접속할 수 있게 공개합니다.',
    result: '플레이 가능한 링크',
  },
  {
    icon: FiBookOpen,
    audience: 'EDUCATION · TOOL',
    title: '학습 도우미',
    copy: '내 공부 방식에 맞는 퀴즈·기록·피드백 도구를 AI와 함께 만듭니다.',
    result: '필요에서 시작한 도구',
  },
];

function WorkspaceVisual() {
  return (
    <div
      className="product-workspace"
      role="img"
      aria-label="프로젝트 폴더를 선택하고 ChatGPT, Claude, Gemini와 대화해 학교 축제 대기시간 서비스를 만든 뒤 실제 링크로 공개하는 나니아랩 작업 화면"
    >
      <div className="product-workspace__ambient" aria-hidden="true">
        <i />
        <i />
        <i />
      </div>

      <div className="product-workspace__window" aria-hidden="true">
        <header className="product-workspace__topbar">
          <span className="product-window-dots"><i /><i /><i /></span>
          <div className="product-workspace__identity">
            <b>N</b>
            <span>NARNIA AI <small>PROJECT STUDIO</small></span>
          </div>
          <span className="product-workspace__secure"><i /> SAFE WORKSPACE</span>
        </header>

        <div className="product-workspace__body">
          <aside className="product-workspace__sidebar">
            <div className="product-workspace__sidebar-title">
              <span>PROJECTS</span>
              <b>+</b>
            </div>
            <div className="product-workspace__project product-workspace__project--active">
              <i><FiFolder /></i>
              <span>festival-now<small>작업 중</small></span>
            </div>
            <div className="product-workspace__project">
              <i><FiFolder /></i>
              <span>study-helper<small>어제</small></span>
            </div>
            <div className="product-workspace__project">
              <i><FiFolder /></i>
              <span>my-first-game<small>3일 전</small></span>
            </div>
            <div className="product-workspace__sidebar-foot">
              <FiShield />
              <span>교육용 보호 환경</span>
            </div>
          </aside>

          <section className="product-workspace__conversation">
            <header>
              <span>AI DOCK</span>
              <div className="product-model-switcher">
                <b className="is-active"><i>G</i> ChatGPT</b>
                <b><i>C</i> Claude</b>
                <b><i>G</i> Gemini</b>
              </div>
            </header>

            <div className="product-conversation-thread">
              <article className="product-message product-message--user">
                <span>나</span>
                <p>학교 축제 대기시간을 한눈에 보는 서비스를 만들고 싶어요.</p>
              </article>
              <article className="product-message product-message--ai">
                <span>N</span>
                <div>
                  <small>NARNIA AI</small>
                  <p>좋아요. 부스별 상태와 기다리는 시간을 먼저 화면으로 만들게요.</p>
                </div>
              </article>
              <div className="product-build-progress">
                <span><FiCheck /> 화면 설계</span>
                <span><FiCheck /> 데이터 연결</span>
                <span className="is-building"><i /> 공개 준비</span>
              </div>
            </div>

            <footer>
              <span>아이디어를 이어서 말해보세요.</span>
              <b><FiArrowUpRight /></b>
            </footer>
          </section>

          <aside className="product-workspace__preview">
            <header>
              <span><i /> LIVE PREVIEW</span>
              <b>↗</b>
            </header>
            <div className="product-preview-app">
              <small>STUDENT PROJECT</small>
              <strong>축제 NOW</strong>
              <p>기다리지 말고, 지금 확인하세요.</p>
              <div>
                <span>과학 체험 <b>5분</b></span>
                <span>쿠킹 클래스 <b>12분</b></span>
                <span>보드게임 <b>여유</b></span>
              </div>
            </div>
            <footer>
              <span><i /> narnialab.app/festival-now</span>
            </footer>
          </aside>
        </div>
      </div>

      <div className="product-deploy-toast" aria-hidden="true">
        <span><FiGlobe /></span>
        <div><small>DEPLOY COMPLETE</small><strong>서비스가 공개됐어요.</strong></div>
        <FiCheck />
      </div>
    </div>
  );
}

export default function ProductPage() {
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    document.documentElement.classList.add('product-page-active');
    return () => document.documentElement.classList.remove('product-page-active');
  }, []);

  return (
    <MotionConfig reducedMotion="user">
      <div className="product-page">
        <Helmet>
          <title>나니아랩 제품소개 | 대화로 만들고 링크로 공개하는 AI 개발 환경</title>
          <meta
            name="description"
            content="ChatGPT, Claude, Gemini를 한 공간에서 사용하고 복잡한 설치 없이 아이디어를 실제 프로그램과 공개 링크로 완성하는 나니아랩 AI 개발 환경."
          />
          <link rel="canonical" href="https://narnia-lab.com/product" />
          <meta property="og:title" content="나니아랩 제품소개 | AI 개발 환경" />
          <meta
            property="og:description"
            content="아이디어를 말하면 화면이 되고, 한 번의 흐름으로 실제 링크까지 공개됩니다."
          />
          <meta property="og:url" content="https://narnia-lab.com/product" />
        </Helmet>

        <a className="product-skip-link" href="#product-main">본문으로 바로가기</a>

        <main id="product-main">
          <section className="product-hero" id="product-overview" aria-labelledby="product-hero-title">
            <div className="product-hero__grid" aria-hidden="true" />
            <div className="product-hero__orb product-hero__orb--one" aria-hidden="true" />
            <div className="product-hero__orb product-hero__orb--two" aria-hidden="true" />

            <div className="product-shell product-hero__layout">
              <motion.div
                className="product-hero__copy"
                initial={prefersReducedMotion ? false : 'hidden'}
                animate="visible"
                variants={revealGroup}
              >
                <motion.span className="product-eyebrow" variants={reveal}>
                  <i />
                  NARNIA AI WORKSPACE
                </motion.span>
                <motion.h1 id="product-hero-title" variants={reveal}>
                  말하면 만들고,
                  <br />
                  <span>누르면 공개됩니다.</span>
                </motion.h1>
                <motion.p variants={reveal}>
                  ChatGPT·Claude·Gemini를 한 공간에서 만나고,
                  복잡한 설치 없이 아이디어를 작동하는 프로그램으로 완성하세요.
                </motion.p>
                <motion.div className="product-hero__actions" variants={reveal}>
                  <a
                    className="product-button product-button--primary"
                    href={SERVICE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    나니아랩 시작하기
                    <FiArrowUpRight />
                  </a>
                </motion.div>
                <motion.div className="product-hero__proof" variants={reveal}>
                  <span><FiCpu /> 3개의 대표 AI</span>
                  <span><FiTerminal /> 설치 없이 시작</span>
                  <span><FiGlobe /> 실제 링크로 공개</span>
                </motion.div>
              </motion.div>

              <motion.div
                className="product-hero__visual"
                initial={prefersReducedMotion ? false : { opacity: 0, x: 54, scale: 0.96 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                transition={{ duration: 1, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
              >
                <WorkspaceVisual />
              </motion.div>
            </div>

            <a className="product-hero__scroll" href="#product-flow">
              <span>SEE HOW IT WORKS</span>
              <i />
            </a>
          </section>

          <section className="product-flow product-section" id="product-flow" aria-labelledby="product-flow-title">
            <div className="product-shell">
              <motion.div
                className="product-heading product-heading--split"
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.36 }}
              >
                <div>
                  <span className="product-kicker"><FiZap /> ZERO SETUP, ONE FLOW</span>
                  <h2 id="product-flow-title">
                    복잡한 준비는 뒤로.
                    <br />
                    <span>아이디어에서 링크까지.</span>
                  </h2>
                </div>
                <p>
                  개발 도구를 설치하고 서버 명령을 외우는 대신,
                  만들고 싶은 것을 설명하는 순간부터 시작합니다.
                </p>
              </motion.div>

              <motion.div
                className="product-flow__compare"
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
              >
                <div className="product-flow__legacy">
                  <span>기존 개발 준비</span>
                  <div>
                    {['개발 도구', 'Git', '터미널', 'DB', '서버', '도메인'].map((item) => (
                      <i key={item}>{item}</i>
                    ))}
                  </div>
                  <strong>시작 전부터 여러 단계</strong>
                </div>
                <FiArrowRight aria-hidden="true" />
                <div className="product-flow__narnia">
                  <span>NARNIA LAB</span>
                  <strong>아이디어를 말하면<br />바로 만드는 화면으로.</strong>
                  <small><i /> READY IN YOUR BROWSER</small>
                </div>
              </motion.div>

              <motion.div
                className="product-flow__steps"
                variants={revealGroup}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.22 }}
                role="list"
              >
                <i className="product-flow__line" aria-hidden="true"><b /></i>
                {flowSteps.map((step) => {
                  const Icon = step.icon;
                  return (
                    <motion.article key={step.number} variants={reveal} role="listitem">
                      <header>
                        <span>{step.number}</span>
                        <small>{step.label}</small>
                        <Icon />
                      </header>
                      <h3>{step.title}</h3>
                      <p>{step.copy}</p>
                    </motion.article>
                  );
                })}
              </motion.div>
            </div>
          </section>

          <section
            className="product-capabilities product-section"
            id="product-capabilities"
            aria-labelledby="product-capabilities-title"
          >
            <div className="product-shell">
              <motion.div
                className="product-heading"
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.36 }}
              >
                <span className="product-kicker"><FiLayers /> BUILT FOR MAKING</span>
                <h2 id="product-capabilities-title">
                  필요한 AI와 도구를,
                  <br />
                  <span>하나의 작업 공간에.</span>
                </h2>
                <p>
                  여러 서비스와 설정 화면을 오가지 않아도
                  생각하고, 만들고, 실행하고, 공유하는 흐름이 이어집니다.
                </p>
              </motion.div>

              <motion.div
                className="product-bento"
                variants={revealGroup}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.18 }}
              >
                <motion.article className="product-bento__models" variants={reveal}>
                  <header>
                    <span>01</span>
                    <small>MULTI AI DOCK</small>
                    <FiCpu />
                  </header>
                  <h3>ChatGPT·Claude·Gemini를<br />상황에 맞게 선택합니다.</h3>
                  <p>초등학생도 이름을 보고 고를 수 있는 단순한 인터페이스로 준비했습니다.</p>
                  <div className="product-model-cards" aria-label="지원 AI">
                    <div className="product-model-card product-model-card--chatgpt">
                      <i>G</i>
                      <span><small>OPENAI</small><strong>ChatGPT</strong></span>
                      <b>READY</b>
                    </div>
                    <div className="product-model-card product-model-card--claude">
                      <i>C</i>
                      <span><small>ANTHROPIC</small><strong>Claude</strong></span>
                      <b>READY</b>
                    </div>
                    <div className="product-model-card product-model-card--gemini">
                      <i>G</i>
                      <span><small>GOOGLE</small><strong>Gemini</strong></span>
                      <b>READY</b>
                    </div>
                  </div>
                </motion.article>

                <motion.article className="product-bento__folder" variants={reveal}>
                  <header>
                    <span>02</span>
                    <small>PROJECT CONTEXT</small>
                    <FiFolder />
                  </header>
                  <h3>폴더를 고르면,<br />AI도 맥락을 이어갑니다.</h3>
                  <div className="product-folder-stack" aria-hidden="true">
                    <span><FiFolder /> festival-now <i>12 files</i></span>
                    <span><FiFolder /> study-helper <i>8 files</i></span>
                    <span><FiFolder /> my-first-game <i>15 files</i></span>
                  </div>
                </motion.article>

                <motion.article className="product-bento__deploy" variants={reveal}>
                  <header>
                    <span>03</span>
                    <small>LIVE DEPLOY</small>
                    <FiGlobe />
                  </header>
                  <h3>실행과 공개를<br />한 화면에서.</h3>
                  <div className="product-live-card" aria-hidden="true">
                    <span><i /> LIVE</span>
                    <strong>festival-now</strong>
                    <small>narnialab.app/festival-now</small>
                    <b><FiArrowUpRight /></b>
                  </div>
                </motion.article>

                <motion.article className="product-bento__safe" variants={reveal}>
                  <header>
                    <span>04</span>
                    <small>SAFE BY DESIGN</small>
                    <FiShield />
                  </header>
                  <div>
                    <h3>처음 만나는 AI도<br />안심할 수 있게.</h3>
                    <p>
                      교육 환경에 맞춘 보호 장치와 쉬운 안내로,
                      어린 사용자도 자신의 프로젝트에 집중합니다.
                    </p>
                    <ul>
                      <li><FiCheck /> 쉬운 모델 선택</li>
                      <li><FiCheck /> 프로젝트별 작업 공간</li>
                      <li><FiCheck /> 안전한 공유 흐름</li>
                    </ul>
                  </div>
                  <div className="product-safe-orbit" aria-hidden="true">
                    <i />
                    <i />
                    <i />
                    <b>N</b>
                    <span><FiShield /></span>
                  </div>
                </motion.article>
              </motion.div>
            </div>
          </section>

          <section
            className="product-interface product-section"
            id="product-interface"
            aria-labelledby="product-interface-title"
          >
            <div className="product-shell">
              <motion.div
                className="product-heading product-heading--split"
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.35 }}
              >
                <div>
                  <span className="product-kicker"><FiMonitor /> THE REAL WORKSPACE</span>
                  <h2 id="product-interface-title">
                    기능은 많아도,
                    <br />
                    <span>사용 경험은 단순하게.</span>
                  </h2>
                </div>
                <p>
                  실제 나니아랩 화면은 프로젝트의 시작부터 피드백과 공유까지
                  필요한 순간에 필요한 도구만 보여줍니다.
                </p>
              </motion.div>

              <div className="product-tour">
                {productTour.map((item, index) => (
                  <motion.article
                    className={`product-tour__item product-tour__item--${item.tone}`}
                    key={item.number}
                    variants={reveal}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.22 }}
                  >
                    <div className="product-tour__copy">
                      <span>{item.number} · {item.label}</span>
                      <h3>{item.title.split('\n').map((line) => <span key={line}>{line}</span>)}</h3>
                      <p>{item.copy}</p>
                      <ul>
                        {item.tags.map((tag) => <li key={tag}><FiCheck /> {tag}</li>)}
                      </ul>
                    </div>
                    <div className="product-tour__visual">
                      <header>
                        <span className="product-window-dots" aria-hidden="true"><i /><i /><i /></span>
                        <small>app.narnia-lab.com</small>
                        <b><FiRefreshCw /></b>
                      </header>
                      <div>
                        <img src={item.image} alt={item.imageAlt} loading="lazy" />
                      </div>
                    </div>
                    <i className="product-tour__number" aria-hidden="true">{item.number}</i>
                  </motion.article>
                ))}
              </div>
            </div>
          </section>

          <section
            className="product-use-cases product-section"
            id="product-use-cases"
            aria-labelledby="product-use-cases-title"
          >
            <div className="product-shell">
              <motion.div
                className="product-heading product-heading--split"
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.35 }}
              >
                <div>
                  <span className="product-kicker"><FiGrid /> MADE WITH NARNIA</span>
                  <h2 id="product-use-cases-title">
                    작은 필요가,
                    <br />
                    <span>실제 서비스가 됩니다.</span>
                  </h2>
                </div>
                <p>
                  학생의 호기심부터 현장의 업무 문제까지,
                  누구나 자신에게 필요한 프로그램을 직접 만들 수 있습니다.
                </p>
              </motion.div>

              <motion.div
                className="product-use-grid"
                variants={revealGroup}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.16 }}
                role="list"
              >
                {useCases.map((item) => {
                  const Icon = item.icon;
                  return (
                    <motion.article
                      className={item.size === 'wide' ? 'is-wide' : undefined}
                      key={item.title}
                      variants={reveal}
                      role="listitem"
                    >
                      <header>
                        <span><Icon /></span>
                        <small>{item.audience}</small>
                      </header>
                      <h3>{item.title}</h3>
                      <p>{item.copy}</p>
                      <footer>
                        <span><i /> {item.result}</span>
                        <FiArrowUpRight />
                      </footer>
                    </motion.article>
                  );
                })}
              </motion.div>
            </div>
          </section>

          <section className="product-final product-section" id="product-start" aria-labelledby="product-final-title">
            <div className="product-final__grid" aria-hidden="true" />
            <div className="product-shell product-final__layout">
              <motion.div
                className="product-final__copy"
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.35 }}
              >
                <span className="product-kicker"><FiZap /> READY TO CREATE</span>
                <h2 id="product-final-title">
                  아이디어가 있다면,
                  <br />
                  <span>이미 시작할 준비가 됐습니다.</span>
                </h2>
                <p>
                  개발 지식보다 만들고 싶은 이유가 먼저입니다.
                  나니아랩에서 첫 대화를 시작해 보세요.
                </p>
                <div className="product-final__actions">
                  <a
                    className="product-button product-button--light"
                    href={SERVICE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    서비스 바로가기
                    <FiArrowUpRight />
                  </a>
                  <Link className="product-button product-button--glass" to="/education">
                    교육 방식 보기
                    <FiArrowRight />
                  </Link>
                </div>
              </motion.div>

              <motion.div
                className="product-final__visual"
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                aria-hidden="true"
              >
                <div className="product-launch-orbit">
                  <i />
                  <i />
                  <i />
                  <b>N</b>
                </div>
                <div className="product-launch-card product-launch-card--idea">
                  <span>01</span>
                  <small>IDEA</small>
                  <strong>무엇을 만들까요?</strong>
                </div>
                <div className="product-launch-card product-launch-card--build">
                  <span>02</span>
                  <small>BUILD</small>
                  <strong>AI와 만드는 중</strong>
                </div>
                <div className="product-launch-card product-launch-card--live">
                  <span><i /> LIVE</span>
                  <small>PUBLIC LINK</small>
                  <strong>narnialab.app/my-project</strong>
                </div>
              </motion.div>
            </div>
          </section>
        </main>
      </div>
    </MotionConfig>
  );
}
